import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal } from 'lucide-react';
import { FaInstagram } from 'react-icons/fa';
import { PreviewCardShell } from './PreviewCardShell';

interface InstagramCarouselMockupProps {
  caption: string;
  likes?: string;
  comments?: string;
  username?: string;
  slideCount?: string;
  currentSlide?: string;
  previewLabel?: string;
}

export const InstagramCarouselMockup = ({
  caption,
  likes = '0',
  comments = '0',
  username = '@username',
  slideCount = '3',
  currentSlide = '1',
  previewLabel = 'Instagram Carousel Preview',
}: InstagramCarouselMockupProps) => {
  const totalSlides = parseInt(slideCount) || 3;
  const current = parseInt(currentSlide) || 1;

  return (
    <PreviewCardShell
      className="mx-auto mb-3 w-full max-w-md"
      badgeClassName="bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737]"
      badge={<FaInstagram className="h-4 w-4 text-white" />}
      label={previewLabel}
    >
        {/* Post header */}
        <div className="flex items-center justify-between p-3 border-b border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center gap-2">
            {/* Profile pic */}
            <div className="w-8 h-8 rounded-full p-0.5 bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]">
              <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-400 to-pink-400" />
            </div>
            <div className="text-sm font-semibold text-zinc-50">{username}</div>
          </div>
          <MoreHorizontal className="w-5 h-5 text-zinc-700 dark:text-zinc-300" />
        </div>

        {/* Carousel - 1:1 square aspect ratio */}
        <div className="relative bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900" style={{ aspectRatio: '1/1' }}>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-white/30 rounded-lg mx-auto mb-2" />
              <div className="text-white/60 text-sm font-semibold">
                Slide {current}/{totalSlides}
              </div>
            </div>
          </div>

          {/* Slide indicators */}
          <div className="absolute top-3 left-0 right-0 flex justify-center gap-1 px-3">
            {Array.from({ length: totalSlides }).map((_, idx) => (
              <div
                key={idx}
                className={`h-0.5 flex-1 rounded-full transition-colors ${
                  idx + 1 === current
                    ? 'bg-white'
                    : 'bg-white/40'
                }`}
              />
            ))}
          </div>

          {/* Slide counter badge */}
          <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
            {current}/{totalSlides}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between p-3 border-b border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center gap-4">
            <Heart className="h-6 w-6 text-zinc-100" />
            <MessageCircle className="h-6 w-6 text-zinc-100" />
            <Send className="h-6 w-6 text-zinc-100" />
          </div>
          <Bookmark className="h-6 w-6 text-zinc-100" />
        </div>

        {/* Likes and caption */}
        <div className="px-3 pb-3">
          <div className="mb-1 text-sm font-semibold text-zinc-50">{likes} likes</div>
          <div className="text-sm text-zinc-100">
            <span className="mr-1 font-semibold text-zinc-50">{username}</span>
            <span className="line-clamp-2">{caption}</span>
          </div>
          {comments !== '0' && (
            <div className="mt-1 text-sm text-zinc-400">
              View all {comments} comments
            </div>
          )}
        </div>
    </PreviewCardShell>
  );
};
